import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { ArrowLeft, TrendingUp, Users, BookOpen, ShoppingBag, User, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const PlatformAnalytics = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState({
    revenue_by_month: [],
    rentals_by_month: [],
    new_books: 0,
    active_users: 0,
    most_rented: [],
    category_stats: []
  });
  const [ownerAnalytics, setOwnerAnalytics] = useState({
    owner_analytics: [],
    summary: {
      total_owners: 0,
      total_revenue: 0,
      total_orders: 0,
      total_books: 0,
      avg_revenue_per_owner: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [expandedOwner, setExpandedOwner] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [analyticsRes, ownerRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/analytics/owners`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setAnalytics(analyticsRes.data);
      setOwnerAnalytics(ownerRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const toggleOwnerExpand = (ownerId) => {
    setExpandedOwner(expandedOwner === ownerId ? null : ownerId);
  };

  return (
    <div className="min-h-screen" style={{background: 'var(--background)'}}>
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/super-admin')} className="flex items-center space-x-2">
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Dashboard</span>
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold mb-8" style={{color: 'var(--foreground)'}}>Platform Analytics</h1>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-md p-6">
            <TrendingUp className="h-8 w-8 mb-3" style={{color: 'var(--primary)'}} />
            <p className="text-sm" style={{color: 'var(--foreground)'}}>Monthly Growth</p>
            <p className="text-3xl font-bold" style={{color: 'var(--accent)'}}>+12%</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-md p-6">
            <Users className="h-8 w-8 mb-3" style={{color: 'var(--primary)'}} />
            <p className="text-sm" style={{color: 'var(--foreground)'}}>Active Users</p>
            <p className="text-3xl font-bold" style={{color: 'var(--foreground)'}}>{analytics.active_users}</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-md p-6">
            <BookOpen className="h-8 w-8 mb-3" style={{color: 'var(--primary)'}} />
            <p className="text-sm" style={{color: 'var(--foreground)'}}>New Books (This Month)</p>
            <p className="text-3xl font-bold" style={{color: 'var(--foreground)'}}>{analytics.new_books}</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-md p-6">
            <ShoppingBag className="h-8 w-8 mb-3" style={{color: 'var(--primary)'}} />
            <p className="text-sm" style={{color: 'var(--foreground)'}}>Total Rentals</p>
            <p className="text-3xl font-bold" style={{color: 'var(--foreground)'}}>{analytics.rentals_by_month.reduce((a, b) => a + b.count, 0)}</p>
          </div>
        </div>

        {/* Book Owner Analytics Section */}
        <div className="bg-white rounded-2xl shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold" style={{color: 'var(--foreground)'}}>Book Owner Analytics</h2>
              <p className="text-sm text-gray-600 mt-1">Performance breakdown by book owner</p>
            </div>
            <div className="flex items-center space-x-4 text-sm">
              <div className="px-4 py-2 rounded-lg" style={{backgroundColor: 'var(--card)'}}>
                <span className="text-gray-600">Total Owners: </span>
                <span className="font-bold" style={{color: 'var(--foreground)'}}>{ownerAnalytics.summary.total_owners}</span>
              </div>
              <div className="px-4 py-2 rounded-lg" style={{backgroundColor: 'var(--card)'}}>
                <span className="text-gray-600">Avg Revenue: </span>
                <span className="font-bold" style={{color: 'var(--accent)'}}>₹{ownerAnalytics.summary.avg_revenue_per_owner.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {ownerAnalytics.owner_analytics.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No book owner data available yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {ownerAnalytics.owner_analytics.map((owner, index) => (
                <div 
                  key={owner.owner_id} 
                  className="border rounded-xl overflow-hidden"
                  style={{borderColor: index === 0 ? 'var(--accent)' : '#E5E7EB'}}
                >
                  {/* Owner Header */}
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleOwnerExpand(owner.owner_id)}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full" style={{backgroundColor: index === 0 ? 'var(--accent)' : 'var(--card)'}}>
                        <span className="font-bold text-lg" style={{color: index === 0 ? 'white' : 'var(--foreground)'}}>
                          {index + 1}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg" style={{color: 'var(--foreground)'}}>{owner.owner_name}</h3>
                        <p className="text-sm text-gray-600">{owner.total_books} books listed</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-6">
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Orders</p>
                        <p className="font-bold" style={{color: 'var(--foreground)'}}>{owner.total_orders}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Revenue</p>
                        <p className="font-bold text-lg" style={{color: 'var(--accent)'}}>₹{owner.total_revenue.toFixed(2)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Share</p>
                        <p className="font-bold" style={{color: 'var(--primary)'}}>
                          {ownerAnalytics.summary.total_revenue > 0 
                            ? ((owner.total_revenue / ownerAnalytics.summary.total_revenue) * 100).toFixed(1) 
                            : 0}%
                        </p>
                      </div>
                      {expandedOwner === owner.owner_id ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Book Details */}
                  {expandedOwner === owner.owner_id && owner.books.length > 0 && (
                    <div className="border-t px-4 py-3" style={{backgroundColor: 'var(--card)'}}>
                      <h4 className="font-semibold text-sm mb-3" style={{color: 'var(--foreground)'}}>Book Performance</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {owner.books.map((book) => (
                          <div key={book.id} className="bg-white rounded-lg p-3 flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate" style={{color: 'var(--foreground)'}}>{book.title}</p>
                              <p className="text-xs text-gray-500">{book.rental_count} rentals</p>
                            </div>
                            <div className="ml-3 text-right">
                              <p className="font-bold text-sm" style={{color: 'var(--accent)'}}>₹{book.revenue.toFixed(2)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Most Rented and Category Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white rounded-2xl shadow-md p-6">
            <h2 className="text-2xl font-bold mb-4" style={{color: 'var(--foreground)'}}>Most Rented Books</h2>
            <div className="space-y-3">
              {analytics.most_rented.map((book, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg" style={{backgroundColor: 'var(--card)'}}>
                  <div>
                    <p className="font-semibold" style={{color: 'var(--foreground)'}}>{book.title}</p>
                    <p className="text-sm text-gray-600">{book.author}</p>
                  </div>
                  <span className="font-bold" style={{color: 'var(--accent)'}}>{book.rental_count} rentals</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-6">
            <h2 className="text-2xl font-bold mb-4" style={{color: 'var(--foreground)'}}>Category-wise Rentals</h2>
            <div className="space-y-3">
              {analytics.category_stats.map((cat, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg" style={{backgroundColor: 'var(--card)'}}>
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">{cat.icon}</span>
                    <span className="font-semibold" style={{color: 'var(--foreground)'}}>{cat.name}</span>
                  </div>
                  <span className="font-bold" style={{color: 'var(--primary)'}}>{cat.count} books</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Revenue Over Time */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <h2 className="text-2xl font-bold mb-4" style={{color: 'var(--foreground)'}}>Revenue Over Time</h2>
          <div className="h-64 flex items-end justify-around space-x-2">
            {analytics.revenue_by_month.map((month, index) => (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div 
                  className="w-full rounded-t-lg" 
                  style={{height: `${(month.revenue / Math.max(...analytics.revenue_by_month.map(m => m.revenue))) * 200}px`, backgroundColor: 'var(--accent)'}}
                />
                <p className="text-xs mt-2 text-gray-600">{month.month}</p>
                <p className="text-sm font-semibold" style={{color: 'var(--foreground)'}}>₹{month.revenue}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformAnalytics;
